import { FC } from 'react';
import { Link } from 'react-router-dom';

interface MainNavTabsProps {
  realm: string;
  environments: string[];
  containPaths: (paths: string[]) => boolean;
  containScopes: (roles: string[]) => boolean;
  pathNameRuntime: () => string | undefined;
  getEnvriomentByFeatureFlag: (environments: string[]) => string | undefined;
}

const TABS = [
  { key: 'build', label: 'Build', testId: 'build-link' },
  { key: 'run', label: 'Run', testId: 'run-link' },
  { key: 'monitor', label: 'Monitor', testId: 'monitor-link' }
] as const;

type TabKey = (typeof TABS)[number]['key'];

const tabClassName = (isSelected: boolean) =>
  `flex h-8 items-center rounded-md px-2 text-[14px] uppercase leading-5 tracking-[1px] outline-none transition-colors ${
    isSelected
      ? 'bg-surface font-bold text-secondary shadow-small'
      : 'font-semibold text-on-surface-variant hover:bg-surface-container-low focus-visible:bg-surface-container-low'
  }`;

export const MainNavTabs: FC<MainNavTabsProps> = ({
  realm,
  environments,
  containPaths,
  containScopes,
  pathNameRuntime,
  getEnvriomentByFeatureFlag
}) => {
  const monitorEnvironment =
    getEnvriomentByFeatureFlag(environments) || environments[0];
  const runtimePath = pathNameRuntime();

  const pathByTab: Record<TabKey, string> = {
    build: `/${realm}/operation/build/pipelines`,
    run: `/${realm}/operation/run/${runtimePath}`,
    monitor: `/${realm}/operation/monitor/${monitorEnvironment}/overview`
  };

  const isSelectedByTab: Record<TabKey, boolean> = {
    build: containPaths([`${realm}/operation/build`]),
    run: containPaths([`${realm}/operation/run`]),
    monitor: containPaths([`${realm}/operation/monitor`])
  };

  const isDisabledByTab: Record<TabKey, boolean> = {
    build: containScopes(['CAPSULE:READ', 'PIPELINE:READ']),
    run: !runtimePath,
    monitor: !monitorEnvironment
  };

  return (
    <nav
      data-testid='header-main-nav-tabs'
      aria-label='Main navigation'
      className='flex h-10 items-center gap-1 rounded-md bg-surface-dim p-1'
    >
      {TABS.map(({ key, label, testId }) =>
        isDisabledByTab[key] ? (
          <button
            key={key}
            type='button'
            data-testid={testId}
            disabled
            className={tabClassName(isSelectedByTab[key])}
          >
            {label}
          </button>
        ) : (
          <Link
            key={key}
            to={pathByTab[key]}
            data-testid={testId}
            aria-current={isSelectedByTab[key] ? 'page' : undefined}
            className={tabClassName(isSelectedByTab[key])}
          >
            {label}
          </Link>
        )
      )}
    </nav>
  );
};
